import { Page, Layout, Card, Text, Button, InlineGrid, BlockStack } from '@shopify/polaris';
import { useState, useEffect } from 'react';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { createApiUrl } from '../config/api';
import { useApiClient } from '../hooks/useApiClient';

interface Template {
  id: string;
  name: string;
  category: string;
  preview: string;
  style: any;
}

export default function Templates() {
  const navigate = useAppNavigate();
  const api = useApiClient();
  const [templates, setTemplates] = useState<Template[]>([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      // Public endpoint; still use authenticated client to preserve shop/host context.
      const data = await api.get<Template[]>(createApiUrl('api/templates'));
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      alert(`Error fetching templates: ${(error as any)?.message || 'Unknown error'}`);
    }
  };

  const useTemplate = (template: Template) => {
    // Navigate to label editor with template data in state
    navigate('/labels/new', {
      state: {
        template: {
          name: `${template.name} (from template)`,
          text: template.preview,
          type: 'text',
          enabled: true,
          position: 'top-left',
          shape: 'rectangle',
          animation: 'fade',
          style: {
            backgroundColor: template.style.backgroundColor || '#ff0000',
            textColor: template.style.textColor || '#ffffff',
            fontSize: template.style.fontSize || 14,
            fontFamily: template.style.fontFamily || 'Arial, sans-serif',
            borderRadius: template.style.borderRadius || 4,
            padding: template.style.padding || '8px 12px',
          },
          conditions: {
            products: [],
            collections: [],
            tags: [],
            stockStatus: 'all',
          },
        },
      },
    });
  };

  return (
    <Page title="Label Templates" backAction={{ onAction: () => navigate('/') }}>
      <Layout>
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} gap="400">
            {templates.map((template) => (
              <Card key={template.id}>
                <BlockStack gap="400">
                  <div
                    style={{
                      width: '100%',
                      height: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '8px',
                    }}
                  >
                    <div
                      style={{
                        ...template.style,
                        display: 'inline-block',
                      }}
                    >
                      {template.preview}
                    </div>
                  </div>
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingMd">
                      {template.name}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {template.category}
                    </Text>
                  </BlockStack>
                  <Button onClick={() => useTemplate(template)} fullWidth>
                    Use Template
                  </Button>
                </BlockStack>
              </Card>
            ))}
          </InlineGrid>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
